import React, { useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { getDatabase, ref, set } from 'firebase/database';

const AdminRegister = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1); // Step 1: Company code verification, Step 2: Registration form
  
  const [companyCode, setCompanyCode] = useState('');
  const [adminData, setAdminData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    companyName: ''
  });

  // Company owner code - In production, this should be stored securely in environment variables or database
  const VALID_COMPANY_CODES = ['TRAVEL2026', 'IMMIGRATION2026', 'OWNER2026'];

  const validateName = (name) => /^[a-zA-Z\s]*$/.test(name.trim());
  const validateEmail = (email) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  const validatePassword = (password) => /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(password);
  const validateMobile = (mobile) => /^[6-9]\d{9}$/.test(mobile);

  const handleCodeVerification = (e) => {
    e.preventDefault();
    
    if (!companyCode.trim()) {
      toast.error('Please enter the company code');
      return;
    }

    if (VALID_COMPANY_CODES.includes(companyCode.trim())) {
      toast.success('Company code verified successfully');
      setStep(2);
    } else {
      toast.error('Invalid company code. Only company owners can register as admin.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!adminData.firstName || !adminData.lastName) {
      toast.error('Name fields are required');
      return;
    }
    
    if (!validateName(adminData.firstName) || !validateName(adminData.lastName)) {
      toast.error('Names should not contain numbers or special characters');
      return;
    }

    if (!adminData.companyName.trim()) {
      toast.error('Company name is required');
      return;
    }
    
    if (!validateEmail(adminData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (!validateMobile(adminData.mobile)) {
      toast.error('Please enter a valid 10-digit mobile number starting with 6-9');
      return;
    }
    
    if (!validatePassword(adminData.password)) {
      toast.error('Password must be at least 8 characters with one letter, one number, and one special character');
      return;
    }
    
    if (adminData.password !== adminData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    
    try {
      const auth = getAuth();
      const database = getDatabase();
      
      const userCredential = await createUserWithEmailAndPassword(auth, adminData.email, adminData.password);
      const uid = userCredential.user.uid;

      await set(ref(database, `admins/${uid}`), {
        firstName: adminData.firstName,
        lastName: adminData.lastName,
        email: adminData.email,
        mobile: adminData.mobile,
        companyName: adminData.companyName,
        companyCode: companyCode,
        uid: uid,
        role: 'admin',
        createdAt: new Date().toISOString(),
      });
    
      toast.success('Admin registration successful!');
      
      // Auto-login after registration
      localStorage.setItem('userId', uid);
      localStorage.setItem('userRole', 'admin');
      localStorage.setItem('userEmail', adminData.email);
      localStorage.setItem('userName', `${adminData.firstName} ${adminData.lastName}`);
      localStorage.setItem('companyName', adminData.companyName);
      
      setTimeout(() => {
        window.location.href = '/admin-dashboard';
      }, 2000);
      
      setLoading(false);
      setAdminData({
        firstName: '',
        lastName: '',
        email: '',
        mobile: '',
        password: '',
        confirmPassword: '',
        companyName: ''
      });
      setCompanyCode('');
      
    } catch (error) {
      setLoading(false);
      if (error.code === 'auth/email-already-in-use') {
        toast.error('Email is already registered. Please use Forgot Password or try a different email.');
      } else if (error.code === 'auth/weak-password') {
        toast.error('Password is too weak. Please use a stronger password.');
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Invalid email address format.');
      } else {
        toast.error('Registration failed: ' + error.message);
      }
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#1e293b', padding: '20px' }}>
      <ToastContainer />
      <div style={{ maxWidth: '500px', margin: '0 auto', paddingTop: '60px' }}>
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          padding: '40px', 
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)' 
        }}>
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <div style={{ 
              width: '60px', 
              height: '60px', 
              backgroundColor: '#dc2626', 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <span style={{ fontSize: '30px' }}>🔐</span>
            </div>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1F2937', marginBottom: '8px' }}>
              {step === 1 ? 'Admin Access' : 'Admin Registration'}
            </h1>
            <p style={{ fontSize: '14px', color: '#6B7280' }}>
              {step === 1 ? 'Verify company ownership to continue' : 'Complete your admin profile'}
            </p>
          </div>
          
          {step === 1 ? (
            <form onSubmit={handleCodeVerification}>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                  Company Code <span style={{ color: 'red' }}>*</span>
                </label>
                <input 
                  type="text"
                  value={companyCode}
                  onChange={(e) => setCompanyCode(e.target.value)}
                  placeholder="Enter company verification code"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '10px',
                    fontSize: '14px',
                    outline: 'none',
                    textTransform: 'uppercase'
                  }}
                  required
                />
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>
                  This code is provided to company owners only
                </p>
              </div>
              
              <button 
                type="submit"
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  color: 'white',
                  fontSize: '16px',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: '#dc2626'
                }}
              >
                Verify Code
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                  Company Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input 
                  type="text"
                  value={adminData.companyName}
                  onChange={(e) => setAdminData({...adminData, companyName: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '10px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    First Name <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input 
                    type="text"
                    value={adminData.firstName}
                    onChange={(e) => setAdminData({...adminData, firstName: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #E5E7EB',
                      borderRadius: '10px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                    required
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    Last Name <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input 
                    type="text"
                    value={adminData.lastName}
                    onChange={(e) => setAdminData({...adminData, lastName: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #E5E7EB',
                      borderRadius: '10px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                  Email Address <span style={{ color: 'red' }}>*</span>
                </label>
                <input 
                  type="email"
                  value={adminData.email}
                  onChange={(e) => setAdminData({...adminData, email: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '10px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                  Mobile Number <span style={{ color: 'red' }}>*</span>
                </label>
                <input 
                  type="tel"
                  value={adminData.mobile}
                  onChange={(e) => setAdminData({...adminData, mobile: e.target.value})}
                  maxLength="10"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '10px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                  Password <span style={{ color: 'red' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input 
                    type={showPassword ? 'text' : 'password'}
                    value={adminData.password}
                    onChange={(e) => setAdminData({...adminData, password: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 48px 12px 12px',
                      border: '2px solid #E5E7EB',
                      borderRadius: '10px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#9CA3AF',
                      padding: '4px'
                    }}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                  Confirm Password <span style={{ color: 'red' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input 
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={adminData.confirmPassword}
                    onChange={(e) => setAdminData({...adminData, confirmPassword: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 48px 12px 12px',
                      border: '2px solid #E5E7EB',
                      borderRadius: '10px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#9CA3AF',
                      padding: '4px'
                    }}
                  >
                    {showConfirmPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
              
              <button 
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  color: 'white',
                  fontSize: '16px',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  backgroundColor: loading ? '#9CA3AF' : '#dc2626'
                }}
              >
                {loading ? 'Registering...' : 'Register as Admin'}
              </button>

              <button 
                type="button"
                onClick={() => setStep(1)}
                style={{
                  width: '100%',
                  marginTop: '12px',
                  padding: '12px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  color: '#6B7280',
                  fontSize: '14px',
                  border: '2px solid #E5E7EB',
                  cursor: 'pointer',
                  backgroundColor: 'white'
                }}
              >
                Back
              </button>
            </form>
          )}
          
          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <p style={{ color: '#6B7280', fontSize: '14px' }}>
              Already have an admin account? <a href="/admin-login" style={{ color: '#dc2626', fontWeight: 600 }}>Login here</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminRegister;
